import React, { useEffect, useState, useMemo } from "react";
import { secureStorage } from "../../../utils/secureStorage";
import { useAuth } from "../../../utils/useAuth";
import Container from "../../../utils/Container";
import { Link, useNavigate } from "react-router";
import { Checkbox, Switch, Typography } from "@mui/joy";
import toast from "react-hot-toast";
import successMas from "../../../assets/failed.png";
import PaymentMethod from "./sections/PaymentMethod";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Autoplay, Navigation } from "swiper/modules";
import { FaBed, FaBedPulse } from "react-icons/fa6";
import {
  MdFastfood,
  MdOutlineBathroom,
  MdOutlineBedroomParent,
  MdOutlineViewModule,
} from "react-icons/md";
import { SlDirections } from "react-icons/sl";

// Per-room stay total helpers: a mixed-price room carries stayTotal/stayTax
// (already the full-stay figure); a uniform room uses discountedPrice * nights.
const getRoomStayTotal = (item, nights) =>
  item.stayTotal != null
    ? item.stayTotal
    : (item.discountedPrice || 0) * nights;

const getRoomStayTax = (item, nights) =>
  item.stayTax != null
    ? item.stayTax
    : (item.discountedPrice * ((item.tax || 0) / 100) || 0) * nights;

const getRoomActualTotal = (item, nights) =>
  item.stayNights != null
    ? (item.actualPrice || 0) * item.stayNights
    : (item.actualPrice || 0) * nights;

const Booking = () => {
  const { isLoggedIn, user } = useAuth();
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const hotelInfo = useMemo(() => secureStorage.getItem("hotelInfo") || {}, []);
  const bookedDate = useMemo(
    () => secureStorage.getItem("bookedDate") || {},
    [],
  );
  const bookedHotel = useMemo(
    () => secureStorage.getItem("bookedHotel") || [],
    [],
  );

  const totalAdults = bookedHotel.reduce(
    (sum, r) => sum + Number(r.adults || 0),
    0,
  );

  const totalChildren = bookedHotel.reduce(
    (sum, r) => sum + Number(r.children || 0),
    0,
  );

  const nights =
    (new Date(bookedDate.checkOut) - new Date(bookedDate.checkIn)) /
    (1000 * 60 * 60 * 24);
  const generateBookingId = () =>
    Math.random().toString(36).substring(2, 10).toUpperCase();
  const prefix = "DH-";
  const randomNumber = Math.floor(100000 + Math.random() * 900000); // 6-digit number
  const code = `${prefix}${randomNumber}`;
  const [FormData, setFormData] = useState({
    invoiceId: code,
    transactionId: generateBookingId(),
    invoiceFor: "Hotel",
    type: "Hotel",
    hotelId: "",
    checkIn: "",
    checkOut: "",
    totalAmount: 0,
    discountedAmount: 0,
    taxAmount: 0,
    paidAmount: 0,
    paymentNature: "Online",
    paymentStatus: "Unpaid",
    paymentMethod: 0,
    orderBy: "",
    primaryPhoneNumber: "",
    isAirporPickupNeed: 0,
    isExtraBedRequired: 0,
    isRoomOnHigherFloor: 0,
    isRoomDecorationRequired: 0,
    IsLargeBedRequired: 0,
    isTwinBedRequired: 0,
    isSmoking: 0,
    request: "",
    guests: [],
    rooms: [],
    isAgreed: 0,
  });
  const [Error, setError] = useState(false);
  const [IsProcessing, setIsProcessing] = useState(false);
  useEffect(() => {
    if (user) {
      setFormData((prevData) => ({
        ...prevData,
        primaryPhoneNumber: user.phoneNumber || "",
      }));
    }
  }, []);
  useEffect(() => {
    if (!bookedHotel || bookedHotel.length === 0) return;

    const generated = bookedHotel.map((room) => ({
      roomId: room.id,
      roomName: room.roomName,
      guestInfo: Array.from({ length: room.adults }, () => ({
        guestName: "",
      })),
    }));

    setFormData((prevData) => {
      return {
        ...prevData,
        guests: generated,
      };
    });
  }, []);
  useEffect(() => {
    if (user) {
      setFormData((prevData) => ({
        ...prevData,
        orderBy: user.id,
      }));
    }
    if (hotelInfo) {
      setFormData((prevData) => ({
        ...prevData,
        hotelId: hotelInfo.hotelId,
      }));
    }
    if (bookedDate) {
      setFormData((prevData) => ({
        ...prevData,
        checkIn: bookedDate.checkIn,
        checkOut: bookedDate.checkOut,
      }));
    }
    if (bookedHotel) {
      setFormData((prevData) => ({
        ...prevData,
        rooms: bookedHotel,
      }));
    }
  }, []);
  const handleGuestChange = (roomIndex, guestIndex, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      updated.guests = updated.guests.map((room, rIdx) => {
        if (rIdx !== roomIndex) return room;
        return {
          ...room,
          guestInfo: room.guestInfo.map((guest, gIdx) =>
            gIdx === guestIndex ? { ...guest, guestName: value } : guest,
          ),
        };
      });
      return updated;
    });
  };
  // ---- Totals (support mixed per-night pricing) ----
  // actual (pre-discount) total across the whole stay
  const totalAmountStay = bookedHotel.reduce(
    (total, item) => total + getRoomActualTotal(item, nights),
    0,
  );
  // discounted room total across the whole stay
  const discountedStay = bookedHotel.reduce(
    (total, item) => total + getRoomStayTotal(item, nights),
    0,
  );
  // tax/service charge across the whole stay
  const totalTaxAmountStay = bookedHotel.reduce(
    (total, item) => total + getRoomStayTax(item, nights),
    0,
  );

  const grandTotalStay = discountedStay + totalTaxAmountStay;

  const finalAmount = (
    grandTotalStay *
    (hotelInfo.commissionRate / 100)
  ).toFixed(0);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      totalAmount: totalAmountStay,
      discountedAmount: discountedStay,
      taxAmount: totalTaxAmountStay,
      paidAmount: finalAmount,
    }));
  }, [
    nights,
    totalAmountStay,
    discountedStay,
    totalTaxAmountStay,
    finalAmount,
  ]);
  const handleProcessPayment = async () => {
    secureStorage.setItem("HotelBookingInfo", FormData);
    if (!isLoggedIn) {
      navigate("/authentication/signin");
    } else if (
      FormData.guests.some((room) =>
        room.guestInfo.some((guest) => guest.guestName === ""),
      ) &&
      FormData.primaryPhoneNumber === ""
    ) {
      toast.error("Guests and primary phone number are required");
    } else if (FormData.isAgreed === 0) {
      setError(true);
      toast.error("Please click on the checkbox");
    } else {
      setOpen(true);
    }
  };
  const bookingId = generateBookingId();
  const bookedBy = user;
  const hotel = hotelInfo;
  const grandTotal = finalAmount;
  return (
    <>
      {open && FormData.isAgreed && (
        <PaymentMethod
          open={open}
          setOpen={setOpen}
          setFormData={setFormData}
          FormData={FormData}
          bookingId={bookingId}
          bookedBy={bookedBy}
          hotel={hotel}
          grandTotal={grandTotal}
          setError={setError}
          setIsProcessing={setIsProcessing}
        />
      )}
      {bookedHotel.length > 0 ? (
        <div className="w-full h-full bg-[#f0f8fd] py-10 lg:py-10 mb-10 lg:mb-0">
          <Container>
            <div className="p-0 lg:p-8">
              <div className="block lg:flex gap-3">
                <div className="flex-1 p-0 lg:px-8 rounded-lg space-y-5">
                  {isLoggedIn === false && (
                    <div className="p-4 bg-white border rounded-lg">
                      <p className="text-black/70 text-sm">
                        <Link to={"/authentication/signin"}>
                          <span className="text-blue-500 font-bold">
                            Please Login First
                          </span>{" "}
                        </Link>
                        to have booking confirmation and hotel contact
                        information in your email and SMS.
                      </p>
                    </div>
                  )}

                  <div className="p-4 bg-white border rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-md font-bold">
                          {hotelInfo.hotelName}
                        </p>
                        <p className="text-xs text-black/50">
                          Guests: {totalAdults} | Rooms:{" "}
                          {bookedHotel.length || 0}
                        </p>
                      </div>
                      <div className="w-20 h-10 overflow-hidden rounded-lg">
                        <img
                          className="w-full h-full object-contain"
                          src={hotelInfo.hotelLogo}
                          alt=""
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-white border rounded-lg space-y-6">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="place-self-start text-center">
                        <p className="text-sm text-black/60">
                          Check In: {hotelInfo.checkIn}
                        </p>
                        <p className="text-sm text-black/60">
                          {bookedDate.checkIn}
                        </p>
                      </div>

                      <div className="place-self-center w-fit bg-white p-2 border rounded-md text-center">
                        <p className="text-sm text-black font-medium">
                          Nights: {nights}
                        </p>
                      </div>

                      <div className="place-self-end text-center">
                        <p className="text-sm text-black/60">
                          Check Out: {hotelInfo.checkOut}
                        </p>
                        <p className="text-sm text-black/60">
                          {bookedDate.checkOut}
                        </p>
                      </div>
                    </div>
                    {/* 
                    <div className="flex justify-start items-center gap-3">
                      {bookedHotel.map((item, index) => (
                        <div
                          key={index}
                          className="bg-[#e5eaff] p-4 rounded-lg space-y-2"
                        >
                          <p className="text-md font-bold">
                            {item.roomName}{" "}
                            <span className="text-sm font-normal">
                              ({item.roomType})
                            </span>
                          </p>
                          {item.isBreakFastIncluded === 1 && (
                            <p className="text-xs text-black/60">
                              Breakfast included
                            </p>
                          )}
                        </div>
                      ))}
                    </div> */}
                  </div>

                  {/* ============================ */}
                  {/* ROOM OVERVIEW (full details) */}
                  {/* ============================ */}
                  <div className="p-4 bg-white border rounded-lg space-y-4">
                    <div>
                      <p className="font-bold">Room Overview</p>
                      <small className="text-black/50">
                        Complete details of the rooms you're booking
                      </small>
                    </div>

                    <div className="space-y-4">
                      {bookedHotel.map((item, index) => {
                        const roomStayTotal = getRoomStayTotal(item, nights);
                        const roomStayTax = getRoomStayTax(item, nights);
                        const roomActualTotal = getRoomActualTotal(
                          item,
                          nights,
                        );
                        const hasSegments =
                          Array.isArray(item.priceSegments) &&
                          item.priceSegments.length > 1;

                        return (
                          <div
                            key={index}
                            className="border rounded-lg p-3 space-y-3"
                          >
                            <div className="block lg:flex gap-3">
                              {item.images && item.images.length > 0 && (
                                <div className="w-full lg:w-[220px] flex-shrink-0 h-[180px] overflow-hidden rounded-lg">
                                  <Swiper
                                    autoplay={{
                                      delay: 2500,
                                      disableOnInteraction: false,
                                    }}
                                    navigation={true}
                                    modules={[Autoplay, Navigation]}
                                    loop={true}
                                    className="border h-full"
                                  >
                                    {item.images.map((img, imgIdx) => (
                                      <SwiperSlide key={imgIdx}>
                                        <div className="w-full h-[180px] overflow-hidden rounded-lg">
                                          <img
                                            className="w-full h-full object-cover"
                                            src={img.imageLink}
                                            alt={item.roomName}
                                          />
                                        </div>
                                      </SwiperSlide>
                                    ))}
                                  </Swiper>
                                </div>
                              )}

                              <div className="flex-1 space-y-2 pt-3 lg:pt-0">
                                <p className="font-bold text-md">
                                  {item.roomName}{" "}
                                  <span className="text-xs font-normal text-black/50">
                                    ({item.roomType})
                                  </span>
                                </p>

                                <div className="flex flex-wrap justify-start items-center gap-3">
                                  {item.bedSize && (
                                    <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                      <FaBed /> <p>Bed Size: {item.bedSize}</p>
                                    </div>
                                  )}
                                  {item.beds != null && (
                                    <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                      <FaBedPulse /> <p>Beds: {item.beds}</p>
                                    </div>
                                  )}
                                  {item.roomSize && (
                                    <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                      <MdOutlineBedroomParent />{" "}
                                      <p>Room Size: {item.roomSize}/sqf</p>
                                    </div>
                                  )}
                                  {item.bathrooms != null && (
                                    <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                      <MdOutlineBathroom />{" "}
                                      <p>Bathrooms: {item.bathrooms}</p>
                                    </div>
                                  )}
                                  {item.roomView && (
                                    <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                      <MdOutlineViewModule />{" "}
                                      <p>Room View: {item.roomView}</p>
                                    </div>
                                  )}
                                  {item.roomDirection && (
                                    <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                      <SlDirections />{" "}
                                      <p>Direction: {item.roomDirection}</p>
                                    </div>
                                  )}
                                </div>

                                {item.isBreakFastIncluded === 1 && (
                                  <div className="flex justify-start items-center gap-1 text-xs text-black/50">
                                    <MdFastfood />{" "}
                                    <p>Complementary Breakfast included</p>
                                  </div>
                                )}

                                <div className="flex flex-wrap gap-3 text-xs text-black/50">
                                  {item.adults != null && (
                                    <p>Adults: {item.adults}</p>
                                  )}
                                  {item.children != null && (
                                    <p>Children: {item.children}</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* per-night price breakdown when prices differ */}
                            {hasSegments && (
                              <div className="border border-amber-300 bg-amber-50 rounded-md p-3 space-y-2">
                                <p className="text-xs font-semibold text-amber-800">
                                  Price changes during your stay
                                </p>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-[11px] lg:text-xs">
                                    <thead>
                                      <tr className="text-left text-black/50">
                                        <th className="py-1 pr-2">Dates</th>
                                        <th className="py-1 pr-2">Nights</th>
                                        <th className="py-1 pr-2">Per Night</th>
                                        <th className="py-1">Subtotal</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.priceSegments.map((seg, sidx) => (
                                        <tr
                                          key={sidx}
                                          className="border-t border-amber-200"
                                        >
                                          <td className="py-1 pr-2">
                                            {seg.startDate}
                                            {seg.count > 1
                                              ? ` → ${seg.endDate}`
                                              : ""}
                                          </td>
                                          <td className="py-1 pr-2">
                                            {seg.count}
                                          </td>
                                          <td className="py-1 pr-2">
                                            {Number(
                                              seg.discountedPrice,
                                            ).toFixed(0)}{" "}
                                            BDT
                                            {seg.discount > 0 && (
                                              <span className="text-[10px] text-green-600">
                                                {" "}
                                                ({seg.discount}% off)
                                              </span>
                                            )}
                                          </td>
                                          <td className="py-1">
                                            {Number(seg.subtotal).toFixed(0)}{" "}
                                            BDT
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* per-room price summary */}
                            <div className="flex justify-end items-end border-t pt-2">
                              <div className="text-right">
                                {roomActualTotal > roomStayTotal && (
                                  <p className="text-xs line-through text-red-400">
                                    {roomActualTotal.toFixed(0)} BDT
                                  </p>
                                )}
                                <div className="text-xs text-black/50">
                                  {!hasSegments && item.discount > 0 && (
                                    <span className="text-[10px] p-0.5 bg-[#0a2a47] text-white rounded-md">
                                      {item.discount}% off
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm font-bold">
                                  {roomStayTotal.toFixed(0)} BDT
                                </p>
                                <p className="text-[11px] text-black/50">
                                  + {roomStayTax.toFixed(0)} BDT Service Charge
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="p-4 bg-white border rounded-lg space-y-3">
                    <div>
                      <p className="font-bold">Contact Information</p>
                      <small>
                        Please provide valid phone number to receive
                        confirmation
                      </small>
                    </div>
                    <input
                      type="number"
                      value={FormData.primaryPhoneNumber}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          primaryPhoneNumber: e.target.value,
                        }))
                      }
                      className="p-2 rounded-md border w-full focus:outline-none"
                      placeholder="Primary Phone Number"
                    />
                  </div>
                  <div className="p-4 bg-white border rounded-lg">
                    {FormData.guests.map((room, rIndex) => (
                      <div
                        key={room.roomId}
                        className="border p-3 my-3 rounded-md"
                      >
                        <h3 className="font-bold">{room.roomName}</h3>

                        {room.guestInfo.map((guest, gIndex) => (
                          <input
                            key={gIndex}
                            type="text"
                            placeholder={`Guest ${gIndex + 1} Name`}
                            value={guest.guestName}
                            onChange={(e) =>
                              handleGuestChange(rIndex, gIndex, e.target.value)
                            }
                            className="border p-2 w-full rounded my-2 focus:outline-none"
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="p-4 bg-white border rounded-lg space-y-3">
                    <div>
                      <p className="font-bold">Additional Requests</p>
                      <small>
                        (Optional) but your request will be passed to property
                        to full fill
                      </small>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                      <div className="p-3 border rounded-md">
                        <Typography
                          component="label"
                          startDecorator={
                            <Switch
                              checked={FormData.isAirporPickupNeed === 1}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  isAirporPickupNeed: e.target.checked ? 1 : 0,
                                }))
                              }
                              sx={{ ml: 1 }}
                            />
                          }
                        >
                          Airpor Pickup Needed?
                        </Typography>
                      </div>
                      <div className="p-3 border rounded-md">
                        <Typography
                          component="label"
                          startDecorator={
                            <Switch
                              checked={FormData.isExtraBedRequired === 1}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  isExtraBedRequired: e.target.checked ? 1 : 0,
                                }))
                              }
                              sx={{ ml: 1 }}
                            />
                          }
                        >
                          Extra Bed Required?
                        </Typography>
                      </div>
                      <div className="p-3 border rounded-md">
                        <Typography
                          component="label"
                          startDecorator={
                            <Switch
                              checked={FormData.isRoomOnHigherFloor === 1}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  isRoomOnHigherFloor: e.target.checked ? 1 : 0,
                                }))
                              }
                              sx={{ ml: 1 }}
                            />
                          }
                        >
                          Room on Higher Floor?
                        </Typography>
                      </div>
                      <div className="p-3 border rounded-md">
                        <Typography
                          component="label"
                          startDecorator={
                            <Switch
                              checked={FormData.isRoomDecorationRequired === 1}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  isRoomDecorationRequired: e.target.checked
                                    ? 1
                                    : 0,
                                }))
                              }
                              sx={{ ml: 1 }}
                            />
                          }
                        >
                          Room Decoration Required?
                        </Typography>
                      </div>
                      <div className="p-3 border rounded-md">
                        <Typography
                          component="label"
                          startDecorator={
                            <Switch
                              checked={FormData.isSmoking === 1}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  isSmoking: e.target.checked ? 1 : 0,
                                }))
                              }
                              sx={{ ml: 1 }}
                            />
                          }
                        >
                          Smooking Room?
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-white border rounded-lg space-y-3">
                    <textarea
                      rows={4}
                      value={FormData.request}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          request: e.target.value,
                        }))
                      }
                      className="p-2 rounded-md border w-full focus:outline-none"
                      placeholder="Any addition request?"
                    />
                  </div>
                </div>

                <div className="w-full lg:w-[300px] ">
                  <div className=" bg-white rounded-lg p-4 space-y-8">
                    <div className="flex justify-start gap-2">
                      <div className="w-20 h-16 p-1 overflow-hidden rounded-lg">
                        <img
                          className="w-full h-full object-contain"
                          src={hotelInfo.hotelLogo}
                          alt=""
                        />
                      </div>
                      <div>
                        <p className="text-md font-bold">
                          {hotelInfo.hotelName}
                        </p>
                        <p className="text-xs text-black/50">
                          Guests: {totalAdults} | Rooms:{" "}
                          {bookedHotel.length || 0}
                        </p>
                      </div>
                    </div>
                    <div>
                      <hr />
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <div>
                        <p className="p-1 text-start text-sm font-bold">
                          Total Amount
                        </p>
                      </div>
                      <div className="place-self-end">
                        <p className="p-1 text-end text-sm place-self-end">
                          {totalAmountStay.toFixed(0)} BDT
                        </p>
                      </div>
                      <div className="text-nowrap">
                        <p className="p-1 text-start text-sm font-bold">
                          Discounted{" "}
                          {bookedHotel[0]?.priceSegments?.length > 1 ? (
                            <span className="text-[10px] font-normal text-nowrap p-0.5 bg-[#0a2a47] text-white rounded-md">
                              varies
                            </span>
                          ) : (
                            <span className="text-[10px] font-normal text-nowrap p-0.5 bg-[#0a2a47] text-white rounded-md">
                              {bookedHotel[0]?.discount}% off
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="place-self-end">
                        <p className="p-1 text-end text-sm place-self-end">
                          {discountedStay.toFixed(0)} BDT
                        </p>
                      </div>
                      <div>
                        <p className="p-1 text-start text-sm font-bold">
                          Service Charge
                        </p>
                      </div>
                      <div className="place-self-end">
                        <p className="p-1 text-end text-sm place-self-end">
                          {totalTaxAmountStay.toFixed(0)} BDT
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="bg-[#0a1a46] text-center p-2 rounded-lg text-white text-sm font-bold">
                        Grand Total: <br />{" "}
                        <span className="text-lg">
                          {grandTotalStay.toFixed(0)} BDT
                        </span>
                      </p>
                    </div>
                    <div>
                      <div className="py-2 text-center">
                        <p className="text-sm font-bold">Pay partially!</p>
                        <small className="text-xs">
                          And pay rest on your selected hotel! Enjoy
                        </small>
                      </div>
                      <button
                        onClick={handleProcessPayment}
                        className="bg-[#28d4bd] text-center py-3 rounded-lg text-white text-xl font-bold  scale-100 hover:scale-105 transition-all duration-300 w-full"
                      >
                        <p>
                          {IsProcessing ? (
                            "Processing..."
                          ) : (
                            <>
                              {" "}
                              PAY{" "}
                              {(
                                grandTotalStay *
                                (hotelInfo.commissionRate / 100)
                              ).toFixed(0)}{" "}
                              BDT{" "}
                            </>
                          )}
                        </p>
                      </button>
                      <div className="flex justify-start gap-1 items-start py-3 text-xs text-black/50 ">
                        <Checkbox
                          checked={FormData.isAgreed === 1}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              isAgreed: e.target.checked ? 1 : 0,
                            }))
                          }
                          style={
                            Error === true && FormData.isAgreed === 0
                              ? { border: "1px solid red", borderRadius: "4px" }
                              : {}
                          }
                        />

                        <div>
                          <p className="text-xs">
                            Agreed to the{" "}
                            <Link
                              to="/terms-and-conditions"
                              className="text-blue-500 "
                            >
                              Terms and conditions
                            </Link>
                            ,{" "}
                            <Link
                              to="/privacy-policy"
                              className="text-blue-500 "
                            >
                              Privacy Policy
                            </Link>
                            ,{" "}
                            <Link
                              to="/refund-policy"
                              className="text-blue-500 "
                            >
                              Refund Policy
                            </Link>
                            ,{" "}
                            <Link
                              to="/delivery-policy"
                              className="text-blue-500 "
                            >
                              Deliver Policy
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      ) : (
        <div>
          <Container>
            <div className="flex flex-col items-center justify-center h-full py-32">
              <div className="w-[100px] lg:w-52 h-full">
                <img
                  src={successMas}
                  className="w-full h-full object-contain"
                  alt=""
                />
              </div>
              <p className="text-lg font-semibold text-center">
                You have not book any hotel, please do!
              </p>
              <div className="mt-8">
                <button
                  className="bg-[#161f52] hover:bg-[#0a0e25] duration-300 text-white font-bold py-2 px-4 rounded"
                  onClick={() => navigate("/")}
                >
                  Check out now
                </button>
              </div>
            </div>
          </Container>
        </div>
      )}
    </>
  );
};

export default Booking;
